import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { SharjahPoliceEmblem } from '../SharjahPoliceEmblem';
import { PlusIcon, SmartphoneIcon, UserPlusIcon, SendIcon, CheckCircle2Icon, BellIcon } from 'lucide-react';
const PAIR2_LOGO = 'https://cdn.magicpatterns.com/uploads/51QRCG1WJpxN6EwND9yhQF/Pair2_(2).svg';
/* Larger pool of phone numbers — the animation loops through them continuously */
const PHONE_POOL = ['0501234567', '0559876543', '0524567890', '0563210987', '0587654321', '0508881234', '0552223344', '0525556677', '0569990011', '0581112233', '0504445566', '0557778899', '0523334455', '0566667788', '0582221100'];
export function OnboardingSlide() {
  const reduce = useReducedMotion();
  const base = reduce ? {
    duration: 0
  } : undefined;
  const [typedText, setTypedText] = useState('');
  const [entries, setEntries] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState<'typing' | 'adding' | 'waiting'>('waiting');
  const [showNotif, setShowNotif] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  // Auto-scroll to bottom when entries change
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [entries]);
  // Start the animation
  useEffect(() => {
    if (reduce) {
      setEntries(PHONE_POOL.slice(0, 8));
      return;
    }
    const startDelay = setTimeout(() => {
      setPhase('typing');
    }, 1800);
    return () => clearTimeout(startDelay);
  }, [reduce]);
  // Typing phase
  useEffect(() => {
    if (reduce || phase !== 'typing') return;
    const targetPhone = PHONE_POOL[currentIndex % PHONE_POOL.length];
    let charIndex = 0;
    const typeInterval = setInterval(() => {
      charIndex++;
      setTypedText(targetPhone.slice(0, charIndex));
      if (charIndex >= targetPhone.length) {
        clearInterval(typeInterval);
        setTimeout(() => setPhase('adding'), 700);
      }
    }, 55);
    return () => clearInterval(typeInterval);
  }, [phase, currentIndex, reduce]);
  // Adding phase
  useEffect(() => {
    if (reduce || phase !== 'adding') return;
    const addTimeout = setTimeout(() => {
      const phone = PHONE_POOL[currentIndex % PHONE_POOL.length];
      setEntries(prev => [...prev, phone]);
      setTypedText('');
      // Show notification on 2nd and 6th entry
      if (currentIndex === 1 || currentIndex === 5) {
        setShowNotif(true);
        setTimeout(() => setShowNotif(false), 2200);
      }
      // Continue to next number
      setTimeout(() => {
        setCurrentIndex(prev => prev + 1);
        setPhase('typing');
      }, 600);
    }, 200);
    return () => clearTimeout(addTimeout);
  }, [phase, currentIndex, reduce]);
  return <section dir="rtl" className="relative w-screen h-screen overflow-hidden flex flex-col" style={{
    background: '#EDF1F4'
  }} data-id="element-500">
      {/* ===== BACKGROUND ===== */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden data-id="element-501">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[50vmax] h-[50vmax] rounded-full blur-3xl" style={{
        background: 'radial-gradient(circle, rgba(47,169,156,0.035) 0%, transparent 55%)'
      }} data-id="element-502" />
        <div className="absolute inset-0 opacity-[0.015]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='48' height='48' viewBox='0 0 48 48' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h48v48H0V0zm1 1h46v46H1V1z' fill='%230B1328' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`
      }} data-id="element-503" />
      </div>

      {/* ===== TOP BAR ===== */}
      <motion.div initial={{
      opacity: 0,
      y: -14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={base ?? {
      duration: 0.8,
      delay: 0.1,
      ease: [0.22, 1, 0.36, 1]
    }} className="relative z-20 flex items-center justify-between pt-[clamp(1rem,2.5vh,2rem)] px-[clamp(1.5rem,4vw,4rem)]" data-id="element-504">
        <SharjahPoliceEmblem variant="light" data-id="element-505" />
        <img src={PAIR2_LOGO} alt="Pair²" className="h-[clamp(2.25rem,4.5vh,3.25rem)] w-auto" style={{
        filter: 'drop-shadow(0 4px 18px rgba(47,169,156,0.28))'
      }} draggable={false} data-id="element-506" />
      </motion.div>

      {/* ===== TITLE ===== */}
      <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={base ?? {
      duration: 0.8,
      delay: 0.2
    }} className="relative z-10 px-[clamp(2rem,5vw,5rem)] mt-[clamp(0.5rem,1.5vh,1rem)]" data-id="element-507">
        <h2 className="font-arabic-display font-black text-center leading-[1.15]" style={{
        color: '#0B1328',
        fontSize: 'clamp(1.3rem, 2.2vw, 2rem)'
      }} data-id="element-508">
          تسجيل السائقين في منظومة{' '}
          <span style={{
          color: '#2FA99C'
        }} data-id="element-509">
            Pair²
          </span>
        </h2>
        <div className="mx-auto mt-2 h-[2.5px] rounded-full" style={{
        width: 'clamp(3rem, 5vw, 4rem)',
        background: '#2FA99C',
        opacity: 0.45
      }} data-id="element-510" />
      </motion.div>

      {/* ===== MAIN CONTENT ===== */}
      <div className="relative z-10 flex-1 min-h-0 grid px-[clamp(1.5rem,4vw,4rem)] pt-[clamp(0.75rem,2vh,1.25rem)]" style={{
      gridTemplateColumns: '1fr 1.6fr',
      gap: 'clamp(1.5rem, 3vw, 3rem)'
    }} data-id="element-511">
        {/* RIGHT COLUMN (first in RTL) — Text Content */}
        <motion.div initial={{
        opacity: 0,
        x: -14
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={base ?? {
        duration: 0.8,
        delay: 0.35
      }} className="flex flex-col justify-center" data-id="element-512">
          <p className="font-arabic-body font-medium text-right leading-[1.9] mb-[clamp(1.5rem,3vh,2.5rem)]" style={{
          color: '#1A1F2B',
          fontSize: 'clamp(0.88rem, 1.05vw, 1rem)'
        }} data-id="element-513">
            يتم تسجيل السائقين من خلال البوابة الإلكترونية عبر الشركات أو الجهات
            المشغلة بخطوات بسيطة وسريعة
          </p>

          {[{
          icon: UserPlusIcon,
          text: 'إدخال رقم هاتف السائق'
        }, {
          icon: SendIcon,
          text: 'إرسال دعوة تلقائية'
        }, {
          icon: SmartphoneIcon,
          text: 'السائق يحمّل التطبيق ويسجّل'
        }].map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={i} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={base ?? {
            duration: 0.5,
            delay: 0.5 + i * 0.12
          }} className="flex items-center gap-[clamp(0.5rem,0.7vw,0.6rem)] mb-[clamp(0.75rem,1.8vh,1.25rem)]" dir="rtl" data-id="element-514">
                <div className="shrink-0 rounded-full flex items-center justify-center" style={{
              width: 'clamp(1.75rem, 2.4vw, 2.15rem)',
              height: 'clamp(1.75rem, 2.4vw, 2.15rem)',
              background: 'rgba(47,169,156,0.08)',
              border: '1px solid rgba(47,169,156,0.18)'
            }} data-id="element-515">
                  <Icon style={{
                width: 'clamp(0.8rem, 1vw, 0.95rem)',
                height: 'clamp(0.8rem, 1vw, 0.95rem)',
                color: '#2FA99C'
              }} strokeWidth={1.8} data-id="element-516" />
                </div>
                <span className="font-arabic-body font-semibold" style={{
              color: '#1A1F2B',
              fontSize: 'clamp(0.82rem, 1vw, 0.95rem)'
            }} data-id="element-517">
                  {item.text}
                </span>
              </motion.div>;
        })}
        </motion.div>

        {/* LEFT COLUMN (second in RTL) — Portal UI Mockup */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.97
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={base ?? {
        duration: 0.9,
        delay: 0.4
      }} className="flex items-center justify-center min-h-0 relative" data-id="element-518">
          {/* Portal Card */}
          <div className="relative w-full rounded-xl overflow-hidden flex flex-col" style={{
          background: 'rgba(255,255,255,0.95)',
          border: '1px solid rgba(26,31,43,0.08)',
          boxShadow: '0 12px 40px -12px rgba(14,93,85,0.1)',
          height: 'clamp(22rem, 58vh, 34rem)',
          maxWidth: 'clamp(22rem, 38vw, 32rem)'
        }} data-id="element-519">
            {/* Portal Header */}
            <div className="flex items-center gap-2 px-[clamp(0.75rem,1.5vw,1.25rem)] py-[clamp(0.5rem,1vh,0.75rem)] shrink-0" style={{
            background: 'rgba(47,169,156,0.04)',
            borderBottom: '1px solid rgba(47,169,156,0.08)'
          }} data-id="element-520">
              <div className="flex gap-1.5" data-id="element-521">
                <div className="w-2.5 h-2.5 rounded-full bg-red-300/60" data-id="element-522" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-300/60" data-id="element-523" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-300/60" data-id="element-524" />
              </div>
              <span className="font-arabic-body text-[0.7rem] text-[#6E7A8F] mr-2" data-id="element-525">
                إضافة سائق جديد
              </span>
            </div>

            {/* Input Area */}
            <div className="px-[clamp(0.75rem,1.5vw,1.25rem)] pt-[clamp(0.75rem,1.5vh,1rem)] shrink-0" dir="rtl" data-id="element-526">
              <label className="font-arabic-body text-[0.72rem] text-[#6E7A8F] mb-1 block font-medium" data-id="element-527">
                رقم هاتف السائق
              </label>
              <div className="flex items-center gap-2" data-id="element-528">
                <div className="flex-1 rounded-lg px-3 py-2 flex items-center" style={{
                background: '#f8f9fb',
                border: '1px solid rgba(47,169,156,0.2)',
                minHeight: '2.25rem'
              }} data-id="element-529">
                  <span className="font-arabic-body text-[0.82rem]" dir="ltr" style={{
                  color: typedText ? '#0B1328' : '#B0BAC8'
                }} data-id="element-530">
                    {typedText || '05XXXXXXXX'}
                  </span>
                  {phase === 'typing' && <motion.span animate={{
                  opacity: [1, 0]
                }} transition={{
                  duration: 0.6,
                  repeat: Infinity
                }} className="inline-block w-[2px] h-4 bg-[#2FA99C] mr-0.5" data-id="element-531" />}
                </div>
                <button className="shrink-0 rounded-lg px-3 py-2 flex items-center gap-1.5" style={{
                background: '#2FA99C',
                color: '#fff',
                fontSize: '0.78rem',
                opacity: typedText ? 1 : 0.5
              }} data-id="element-532">
                  <PlusIcon size={14} data-id="element-533" />
                  <span className="font-arabic-body font-bold" data-id="element-534">إضافة</span>
                </button>
              </div>
            </div>

            {/* Counter */}
            <div className="px-[clamp(0.75rem,1.5vw,1.25rem)] pt-[clamp(0.4rem,0.8vh,0.5rem)] pb-1 shrink-0 flex items-center justify-between" dir="rtl" data-id="element-535">
              <span className="font-arabic-body text-[0.68rem] text-[#6E7A8F]" data-id="element-536">
                الأرقام المضافة:{' '}
                <span className="font-bold text-[#2FA99C]" data-id="element-537">
                  {entries.length}
                </span>
              </span>
            </div>

            {/* Entries List — fixed height, scrollable */}
            <div ref={listRef} className="flex-1 overflow-y-auto px-[clamp(0.75rem,1.5vw,1.25rem)] pb-[clamp(0.5rem,1vh,0.75rem)]" style={{
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(47,169,156,0.3) transparent'
          }} data-id="element-538">
              <div className="flex flex-col gap-[clamp(0.25rem,0.5vh,0.35rem)]" data-id="element-539">
                <AnimatePresence data-id="element-540">
                  {entries.map((phone, i) => <motion.div key={`entry-${i}`} initial={{
                  opacity: 0,
                  y: -6,
                  height: 0
                }} animate={{
                  opacity: 1,
                  y: 0,
                  height: 'auto'
                }} transition={{
                  duration: 0.35,
                  ease: [0.22, 1, 0.36, 1]
                }} className="flex items-center justify-between rounded-lg px-3 py-[clamp(0.3rem,0.6vh,0.45rem)]" dir="rtl" style={{
                  background: 'rgba(47,169,156,0.03)',
                  border: '1px solid rgba(47,169,156,0.08)'
                }} data-id="element-541">
                      <div className="flex items-center gap-2" data-id="element-542">
                        <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{
                      background: 'rgba(47,169,156,0.1)'
                    }} data-id="element-543">
                          <span className="text-[0.55rem] font-bold text-[#2FA99C]" data-id="element-544">
                            {i + 1}
                          </span>
                        </div>
                        <span className="font-arabic-body text-[0.75rem] font-semibold text-[#0B1328]" dir="ltr" data-id="element-545">
                          {phone}
                        </span>
                      </div>
                      <CheckCircle2Icon size={13} className="text-[#2FA99C] shrink-0" data-id="element-546" />
                    </motion.div>)}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Floating Mobile Notification */}
          <AnimatePresence data-id="element-547">
            {showNotif && <motion.div initial={{
            opacity: 0,
            y: 20,
            x: 20
          }} animate={{
            opacity: 1,
            y: 0,
            x: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.5,
            type: 'spring',
            bounce: 0.3
          }} className="absolute bottom-[clamp(1rem,3vh,2rem)] left-[clamp(0.5rem,2vw,1.5rem)] z-30 rounded-xl px-3 py-2.5 flex items-center gap-2" style={{
            background: 'rgba(255,255,255,0.95)',
            border: '1px solid rgba(47,169,156,0.15)',
            boxShadow: '0 8px 24px -4px rgba(14,93,85,0.15)',
            maxWidth: '14rem'
          }} data-id="element-548">
                <div className="shrink-0 w-7 h-7 rounded-full bg-[#2FA99C]/10 flex items-center justify-center" data-id="element-549">
                  <BellIcon size={14} className="text-[#2FA99C]" data-id="element-550" />
                </div>
                <div className="min-w-0" data-id="element-551">
                  <span className="font-arabic-body text-[0.65rem] font-bold text-[#0B1328] block" data-id="element-552">
                    دعوة من Pair²
                  </span>
                  <span className="font-arabic-body text-[0.58rem] text-[#6E7A8F]" data-id="element-553">
                    تم دعوتك للانضمام كسائق
                  </span>
                </div>
              </motion.div>}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* ===== BOTTOM STATEMENT ===== */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={base ?? {
      duration: 0.8,
      delay: 1.2
    }} className="relative z-20 pb-[clamp(2rem,4vh,3rem)] pt-[clamp(0.5rem,1vh,0.75rem)] flex justify-center" data-id="element-554">
        <p className="font-arabic-body font-bold text-center" style={{
        color: '#0B1328',
        fontSize: 'clamp(0.9rem, 1.1vw, 1.05rem)'
      }} data-id="element-555">
          إدخال السائقين يتم خلال{' '}
          <span style={{
          color: '#2FA99C'
        }} data-id="element-556">
            ثوانٍ
          </span>{' '}
          وبآلية رقمية بالكامل
        </p>
      </motion.div>
    </section>;
}